import { useAuth } from "@dl/web-core-js/auth";
import { PageHeader } from "@dl/web-core-js/shell";
import { columns as defineColumns, DataTable, useServerTable } from "@dl/web-core-js/table";
import { Button, Dialog, Field, Select, toast } from "@dl/web-core-js/ui";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { Plus } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { refusalMessage, SupplierClient } from "../../api/manufacturing";
import { WarehouseClient } from "../../api/product";
import {
  PURCHASE_ORDER_ORIGINS,
  PURCHASE_ORDER_STATUSES,
  PurchaseOrderClient,
  RECEIPT_PROGRESSES,
  type PurchaseOrderDTO,
} from "../../api/purchasing";
import { SupplierLink } from "../../components/SupplierLink";
import { NexpimView } from "../../views";
import { HoldBadge, OriginBadge, ProgressBadge, StatusBadge, useProgressLabel, useStatusLabel } from "./badges";
import { formatCad, formatMoney } from "./replenishment/shared";
import { isoDate } from "../../api/reporting";

/**
 * Purchase orders, paged in the database.
 *
 * <p>Opens on the orders still in play — drafts, those waiting for approval,
 * and those out with suppliers — because that is the working list. 1,767
 * imported orders are ISSUED and more than a year old; SIGM never closed
 * anything, and they stay visible here until someone decides to.</p>
 */
export function PurchaseOrdersPage() {
  const { t } = useTranslation();
  const { http, hasView, canWrite } = useAuth();
  const navigate = useNavigate();
  const allowed = hasView(NexpimView.PURCHASING);
  const orders = useMemo(() => new PurchaseOrderClient(http), [http]);
  const [creating, setCreating] = useState(false);

  const table = useServerTable<PurchaseOrderDTO>({
    queryKey: ["purchasing", "purchase-order"],
    fetchPage: (request) => orders.find(request),
    fetchTotal: () => orders.count(),
    enabled: allowed,
  });

  const statusLabel = useStatusLabel();
  const progressLabel = useProgressLabel();
  const cols = useMemo(() => {
    const c = defineColumns<PurchaseOrderDTO>();
    const options = (values: readonly string[], label: (value: string) => string) =>
      values.map((value) => ({ value, label: label(value) }));
    return [
      c.number("number", (row) => row.number, { headerKey: "column.po.number", filter: "range", priority: 1 }),
      c.custom(
        "supplier_name",
        (row) => row.supplier_name ?? "",
        (row) => <SupplierLink id={row.supplier_id}>{row.supplier_name ?? ""}</SupplierLink>,
        { headerKey: "column.po.supplier", priority: 1 },
      ),
      c.custom(
        "status",
        (row) => row.status,
        (row) => (
          <span className="inline-flex items-center gap-1.5">
            <StatusBadge status={row.status} />
            <HoldBadge onHold={row.is_on_hold} />
          </span>
        ),
        {
          headerKey: "column.status",
          filter: "multiselect",
          filterOptions: options(PURCHASE_ORDER_STATUSES, (value) => statusLabel(value as PurchaseOrderDTO["status"])),
          priority: 2,
        },
      ),
      c.custom("is_on_hold", (row) => (row.is_on_hold ? "true" : "false"), (row) => <HoldBadge onHold={row.is_on_hold} />, {
        headerKey: "column.po.on-hold",
        filter: "select",
        filterOptions: [
          { value: "true", label: t("po.on-hold", "On hold") },
          { value: "false", label: t("po.released", "Released") },
        ],
        defaultVisible: false,
      }),
      c.custom("origin", (row) => row.origin ?? "", (row) => <OriginBadge origin={row.origin} />, {
        headerKey: "column.po.origin",
        filter: "multiselect",
        filterOptions: options(PURCHASE_ORDER_ORIGINS, (value) =>
          ({
            MANUAL: t("po.origin.manual", "Written by hand"),
            REPLENISHMENT: t("po.origin.replenishment", "From replenishment"),
            AUTOMATIC: t("po.origin.automatic", "Automatic draft"),
          })[value] ?? value,
        ),
        defaultVisible: false,
      }),
      c.custom("receipt_progress", (row) => row.receipt_progress ?? "", (row) => <ProgressBadge progress={row.receipt_progress} />, {
        headerKey: "column.po.progress",
        filter: "multiselect",
        filterOptions: options(RECEIPT_PROGRESSES, (value) => progressLabel(value as (typeof RECEIPT_PROGRESSES)[number])),
        priority: 2,
      }),
      c.text("order_date", (row) => row.order_date ?? "", { headerKey: "column.po.order-date", filter: "daterange", priority: 3 }),
      c.text("required_date", (row) => row.required_date ?? "", { headerKey: "column.po.required-date", filter: "daterange" }),
      c.text("last_receipt_date", (row) => row.last_receipt_date ?? "", {
        headerKey: "column.po.last-receipt",
        filter: "daterange",
        defaultVisible: false,
      }),
      c.text("warehouse_code", (row) => row.warehouse_code ?? "", { headerKey: "column.stock.warehouse", filter: "none" }),
      // In the order's own currency, so grouped with cents but no symbol; the CAD column beside it carries one.
      c.custom("subtotal", (row) => row.subtotal ?? null, (row) => <span className="tabular-nums">{formatMoney(row.subtotal)}</span>, {
        headerKey: "column.po.subtotal",
        filter: "range",
        align: "right",
        priority: 3,
      }),
      c.custom("total_cad", (row) => row.total_cad ?? null, (row) => <span className="tabular-nums">{formatCad(row.total_cad)}</span>, {
        headerKey: "column.po.total-cad",
        filter: "range",
        align: "right",
      }),
      c.text("currency", (row) => row.currency, {
        headerKey: "column.currency",
        filter: "select",
        filterOptions: [
          { value: "CAD", label: "CAD" },
          { value: "USD", label: "USD" },
        ],
      }),
      c.number("open_line_count", (row) => row.open_line_count, { headerKey: "column.po.open-lines", filter: "range" }),
      c.text("supplier_reference", (row) => row.supplier_reference ?? "", {
        headerKey: "column.po.supplier-reference",
        defaultVisible: false,
      }),
    ];
  }, [t, statusLabel, progressLabel]);

  if (!allowed) {
    return (
      <>
        <PageHeader title={t("navigation.item.purchase-orders", "Purchase orders")} />
        <div className="p-4 lg:p-6">
          <p className="text-[13px] text-ink-muted">
            {t("error.access-denied.detail", "Your account does not have permission to view this page.")}
          </p>
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={t("navigation.item.purchase-orders", "Purchase orders")}
        subtitle={t("po.subtitle", "What is on order, and what has arrived.")}
        actions={
          canWrite ? (
            <Button variant="primary" onClick={() => setCreating(true)}>
              <Plus className="size-4" />
              {t("po.new", "New order")}
            </Button>
          ) : null
        }
      />
      <DataTable<PurchaseOrderDTO>
        data={table.rows}
        columns={cols}
        getRowId={(row) => String(row.id)}
        stateKey="/purchasing/purchase-orders"
        loading={table.loading}
        server={table.mode}
        initialFilters={[{ id: "status", value: ["DRAFT", "AWAITING_APPROVAL", "ISSUED"] }]}
        onRowClick={(row) =>
          void navigate({ to: "/purchasing/purchase-orders/$orderId", params: { orderId: String(row.id) } })
        }
        emptyMessage={t("table.empty", "Nothing to show")}
      />
      <NewOrderDialog open={creating} onClose={() => setCreating(false)} />
    </>
  );
}

/** A draft needs only who and where; everything else is edited on the order itself. */
function NewOrderDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const navigate = useNavigate();
  const [supplierId, setSupplierId] = useState("");
  const [warehouseId, setWarehouseId] = useState("");
  // Each opening starts blank: a supplier picked and then cancelled must not
  // come back the next time.
  useEffect(() => {
    if (open) {
      setSupplierId("");
      setWarehouseId("");
    }
  }, [open]);

  const suppliers = useQuery({
    queryKey: ["supplier", "all"],
    queryFn: () => new SupplierClient(http).findAll(),
    enabled: open,
    staleTime: 5 * 60_000,
  });
  const warehouses = useQuery({
    queryKey: ["warehouse", "all"],
    queryFn: () => new WarehouseClient(http).findAll(),
    enabled: open,
    staleTime: 30 * 60_000,
  });
  const receiving = (warehouses.data ?? []).filter((w) => w.is_active && w.kind !== "DISTRIBUTION");
  const effectiveWarehouse = warehouseId || String(receiving.find((w) => w.kind === "MANUFACTURING")?.id ?? "");

  const create = useMutation({
    mutationFn: () => {
      const supplier = (suppliers.data ?? []).find((s) => String(s.id) === supplierId);
      return new PurchaseOrderClient(http).create({
        id: 0,
        number: 0,
        supplier_id: Number(supplierId),
        warehouse_id: Number(effectiveWarehouse),
        status: "DRAFT",
        order_date: isoDate(new Date()),
        required_date: null,
        currency: supplier?.currency ?? "CAD",
        supplier_reference: null,
        shipping_cost: 0,
        note: null,
        line_count: 0,
        open_line_count: 0,
      });
    },
    onSuccess: (order) => {
      onClose();
      void navigate({ to: "/purchasing/purchase-orders/$orderId", params: { orderId: String(order.id) } });
    },
    onError: (failure) => toast.error(refusalMessage(failure, t("error.generic", "Something went wrong."))),
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t("po.new", "New order")}
      actions={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("dialog.cancel", "Cancel")}
          </Button>
          <Button variant="primary" disabled={!supplierId || !effectiveWarehouse} loading={create.isPending} onClick={() => create.mutate()}>
            {t("po.create", "Create draft")}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <Field label={t("column.po.supplier", "Supplier")} htmlFor="po-new-supplier" required>
          <Select
            id="po-new-supplier"
            value={supplierId}
            placeholder={t("form.choose", "Choose…")}
            options={(suppliers.data ?? [])
              .filter((s) => s.is_active)
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((s) => ({ value: String(s.id), label: `${s.name} (${s.code})` }))}
            onChange={(event) => setSupplierId(event.target.value)}
          />
        </Field>
        <Field label={t("po.receive-into", "Receive into")} htmlFor="po-new-warehouse" required>
          <Select
            id="po-new-warehouse"
            value={effectiveWarehouse}
            options={receiving.map((w) => ({ value: String(w.id), label: `${w.code} · ${w.name ?? ""}` }))}
            onChange={(event) => setWarehouseId(event.target.value)}
          />
        </Field>
        <p className="text-[12px] text-ink-muted">
          {t("po.new.hint", "It starts as a draft: add lines, then issue it to the supplier. The currency follows the supplier.")}
        </p>
      </div>
    </Dialog>
  );
}
